import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import { useConfirm, useToast } from '@radiology/ui';
import dayjs from 'dayjs';
import {
  COLOR_LABEL,
  getDocumentUrl,
  useAddMaintenance,
  useDeleteDevice,
  useDeviceChildren,
  useUploadDocument,
  type Device,
} from './api';
import { DeviceForm } from './DeviceForm';

/**
 * One device: its detail, its history, and whatever the caller is allowed to do
 * to it.
 *
 * `canEdit` and `canDelete` shape the UI only. The database decides for real —
 * a manager who somehow reached the edit form would have their UPDATE silently
 * match zero rows and their INSERT rejected with 42501. These props exist so
 * they are not shown a button that cannot work.
 */
export function DeviceDialog({
  device,
  canEdit,
  canDelete,
  onClose,
}: {
  device: Device | null;
  canEdit: boolean;
  canDelete: boolean;
  onClose: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState('Completed');
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [error, setError] = useState<string | null>(null);

  const children = useDeviceChildren(device?.id ?? null);
  const addMaintenance = useAddMaintenance();
  const upload = useUploadDocument();
  const remove = useDeleteDevice();
  const confirm = useConfirm();
  const toast = useToast();

  const close = () => {
    setEditing(false);
    setError(null);
    onClose();
  };

  const openDocument = async () => {
    if (!device?.selected_file) return;
    try {
      window.open(await getDocumentUrl(device.selected_file), '_blank', 'noopener');
    } catch {
      toast.error('That document could not be opened.');
    }
  };

  const pickFile = () => {
    if (!device) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      upload.mutate(
        { deviceId: device.id, file },
        {
          onSuccess: () => toast.success('Document attached'),
          onError: (e: Error) => setError(e.message),
        },
      );
    };
    input.click();
  };

  const recordMaintenance = () => {
    if (!device) return;
    addMaintenance.mutate(
      { deviceId: device.id, status, date },
      {
        onSuccess: () => toast.success('Maintenance recorded'),
        onError: (e: Error) => setError(e.message),
      },
    );
  };

  const destroy = async () => {
    if (!device) return;
    const ok = await confirm({
      title: `Delete ${device.equipment_name}?`,
      message:
        'This also deletes its maintenance history, downtime record and trained-employee list. It cannot be undone — marking the device obsolete keeps the history instead.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;

    remove.mutate(device.id, {
      onSuccess: () => {
        toast.success('Device deleted');
        close();
      },
      onError: (e: Error) => setError(e.message),
    });
  };

  const facts = device
    ? [
        { label: 'Manufacturer', value: device.manufacturer || '—' },
        { label: 'Model', value: device.model || '—' },
        { label: 'Serial number', value: device.serial_no || '—' },
        { label: 'HTM', value: device.htm || '—' },
        { label: 'KN', value: device.kn || '—' },
        { label: 'MD', value: device.md || '—' },
        { label: 'PO number', value: device.po_no || '—' },
        { label: 'Site', value: device.site_name || '—' },
        { label: 'Section', value: device.section || '—' },
        { label: 'Location', value: device.location || '—' },
        {
          label: 'Installed',
          value: device.installation_date
            ? dayjs(device.installation_date).format('D MMM YYYY')
            : '—',
        },
        {
          label: 'Age',
          value:
            device.years_in_service === null
              ? '—'
              : `${device.years_in_service} ${device.years_in_service === 1 ? 'year' : 'years'}`,
        },
      ]
    : [];

  return (
    <Dialog open={Boolean(device)} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle variant="h4">{device?.equipment_name}</DialogTitle>
      <DialogContent>
        {device && (
          <Stack gap={2}>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Chip
                size="small"
                label={device.is_obsolete ? 'Obsolete' : 'In service'}
                color={device.is_obsolete ? 'error' : 'success'}
                variant="outlined"
              />
              {device.color_category && COLOR_LABEL[device.color_category] && (
                <Chip size="small" label={COLOR_LABEL[device.color_category]} />
              )}
              {device.selected_file && (
                <Chip size="small" label="Has a document" variant="outlined" />
              )}
            </Stack>

            {error && <Alert severity="error">{error}</Alert>}

            {editing ? (
              <DeviceForm device={device} onSaved={() => setEditing(false)} />
            ) : (
              <>
                <Grid container spacing={1.5}>
                  {facts.map(f => (
                    <Grid key={f.label} size={{ xs: 6, sm: 4 }}>
                      <Typography variant="caption" color="text.secondary">
                        {f.label}
                      </Typography>
                      <Typography variant="body2">{f.value}</Typography>
                    </Grid>
                  ))}
                </Grid>

                {device.capital_equity_information && (
                  <Stack gap={0.25}>
                    <Typography variant="caption" color="text.secondary">
                      Capital equity information
                    </Typography>
                    <Typography variant="body2">{device.capital_equity_information}</Typography>
                  </Stack>
                )}

                <Divider />

                <Stack gap={1}>
                  <Typography variant="overline" color="text.secondary">
                    Maintenance ({children.data?.maintenance.length ?? 0})
                  </Typography>
                  {(children.data?.maintenance.length ?? 0) === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Nothing recorded.
                    </Typography>
                  ) : (
                    children.data?.maintenance.map(m => (
                      <Stack key={m.id} direction="row" justifyContent="space-between">
                        <Typography variant="body2">{m.status ?? '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {m.date ?? ''}
                        </Typography>
                      </Stack>
                    ))
                  )}

                  {canEdit && (
                    <Stack direction="row" gap={1} alignItems="center">
                      <TextField
                        size="small"
                        label="Status"
                        value={status}
                        onChange={e => setStatus(e.target.value)}
                      />
                      <TextField
                        size="small"
                        label="Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={date}
                        onChange={e => setDate(e.target.value)}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={recordMaintenance}
                        disabled={addMaintenance.isPending}
                      >
                        Record
                      </Button>
                    </Stack>
                  )}
                </Stack>

                <Divider />

                <Stack gap={1}>
                  <Typography variant="overline" color="text.secondary">
                    Downtime ({children.data?.downtime.length ?? 0})
                  </Typography>
                  {(children.data?.downtime.length ?? 0) === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No incidents recorded.
                    </Typography>
                  ) : (
                    children.data?.downtime.map(c => (
                      <Stack key={c.id} direction="row" justifyContent="space-between" gap={2}>
                        <Typography variant="body2">{c.component_name ?? '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {c.duration ?? 0} min
                          {c.start_moment
                            ? ` · from ${dayjs(c.start_moment).format('D MMM YYYY')}`
                            : ''}
                        </Typography>
                      </Stack>
                    ))
                  )}
                </Stack>

                {(children.data?.trained.length ?? 0) > 0 && (
                  <>
                    <Divider />
                    <Stack gap={0.5}>
                      <Typography variant="overline" color="text.secondary">
                        Trained staff
                      </Typography>
                      <Stack direction="row" gap={0.75} flexWrap="wrap">
                        {children.data?.trained.map(t => (
                          <Chip key={t.id} size="small" label={t.employee_name} />
                        ))}
                      </Stack>
                    </Stack>
                  </>
                )}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        {canDelete && !editing && (
          <Button onClick={() => void destroy()} color="error" disabled={remove.isPending}>
            Delete
          </Button>
        )}
        <Stack sx={{ flex: 1 }} />
        {device?.selected_file && !editing && (
          <Button onClick={() => void openDocument()} color="inherit">
            Open document
          </Button>
        )}
        {canEdit && !editing && (
          <Button onClick={pickFile} color="inherit" disabled={upload.isPending}>
            {upload.isPending ? 'Uploading…' : 'Attach document'}
          </Button>
        )}
        {canEdit && (
          <Button variant="outlined" onClick={() => setEditing(v => !v)}>
            {editing ? 'Stop editing' : 'Edit'}
          </Button>
        )}
        <Button onClick={close} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
